import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { ChatServiceError } from "@/lib/errors/ChatServiceError";

// Create a simple logger for this component
const componentLogger = {
  error: (message: string, data?: unknown) => {
    console.error(`[ErrorBoundary] ${message}`, data);
  },
};

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component for catching and handling React errors
 * Provides fallback UI for chat-related errors and general application errors
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    componentLogger.error("ErrorBoundary caught an error", {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-red-100 dark:bg-red-900/20">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Что-то пошло не так
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
            {this.state.error instanceof ChatServiceError
              ? this.state.error.getUserMessage()
              : "Произошла непредвиденная ошибка. Пожалуйста, попробуйте еще раз."}
          </p>

          <button
            onClick={this.handleRetry}
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Попробовать снова
          </button>

          {process.env.NODE_ENV === "development" && (
            <details className="mt-6 text-left">
              <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                Детали ошибки (dev mode)
              </summary>
              <pre className="mt-2 text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-auto max-w-full">
                {this.state.error?.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based error boundary for functional components
 */
export const ChatErrorFallback: React.FC<{
  error: ChatServiceError;
  onRetry?: () => void;
}> = ({ error, onRetry }) => {
  return (
    <div className="flex flex-col items-center justify-center p-6 text-center bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-center w-12 h-12 mb-3 rounded-full bg-orange-100 dark:bg-orange-900/20">
        <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
      </div>

      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
        Ошибка чата
      </h3>

      <p className="text-gray-600 dark:text-gray-400 mb-4 max-w-sm">
        {error.getUserMessage()}
      </p>

      {error.isRetryable() && onRetry && (
        <button
          onClick={onRetry}
          className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors duration-200"
        >
          <RefreshCcw className="w-4 h-4 mr-2" />
          Попробовать снова
        </button>
      )}

      {process.env.NODE_ENV === "development" && (
        <div className="mt-4 text-xs text-gray-500">
          Code: {error.code} | Context: {JSON.stringify(error.context, null, 2)}
        </div>
      )}
    </div>
  );
};

/**
 * Loading fallback component with retry option
 */
export const LoadingFallback: React.FC<{
  message?: string;
  onRetry?: () => void;
  showRetry?: boolean;
}> = ({ message = "Загрузка...", onRetry, showRetry = false }) => {
  return (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
      <p className="text-gray-600 dark:text-gray-400 mb-3">{message}</p>

      {showRetry && onRetry && (
        <button
          onClick={onRetry}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          Отменить и попробовать снова
        </button>
      )}
    </div>
  );
};

/**
 * Empty state fallback component
 */
export const EmptyStateFallback: React.FC<{
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  icon?: React.ComponentType<{ className?: string }>;
}> = ({
  title = "Ничего не найдено",
  description = "Пока что здесь пусто",
  action,
  icon: Icon,
}) => {
  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      {Icon && (
        <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-gray-100 dark:bg-gray-800">
          <Icon className="w-8 h-8 text-gray-400 dark:text-gray-600" />
        </div>
      )}

      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
        {title}
      </h3>

      <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
        {description}
      </p>

      {action && (
        <button
          onClick={action.onClick}
          className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
        >
          {action.label}
        </button>
      )}
    </div>
  );
};
